import React, { useCallback, useState } from 'react';

import { reel } from '@/assets/icons';
import { SheetLayout } from '@/components/shared/SheetLayout';
import { Tabs, TabsCount } from '@/components/shared/Tabs';
import { LinkFrom } from '@/config/constants';
import { dashboardUrl, reelsUrl } from '@/config/urls';
import { GetDashboardQuery, useGetDashboardQuery } from '@/graphql';
import { useFormattedTitle } from '@/hooks/useFormattedTitle';
import { useFireAnalyticsPageView } from '@/providers/AnalyticsProvider';
import { Helmet } from 'react-helmet';
import { useNavigate } from 'react-router';

import { Done } from './components/Done';
import { InProgress } from './components/InProgress';
import { PickedForYou } from './components/PickedForYou';
import styles from './Library.module.scss';

export type LibraryTabProps = {
	videoCollections: GetDashboardQuery['getDashboard']['videoCollections'];
};

export const Library: React.FC<{}> = () => {
	const {
		data: dashboard,
		isLoading,
		isRefetching,
		error,
		refetch,
	} = useGetDashboardQuery();
	const navigate = useNavigate();
	const [title] = useFormattedTitle(`Asaph | Library`);

	const [tabIndex, setTabIndex] = useState(0);

	useFireAnalyticsPageView({
		enabled: !!title,
		pageViewArgs: {
			title,
		},
	});

	const handleBackPress = useCallback(
		() => navigate(dashboardUrl),
		[navigate],
	);

	if (error || (!isLoading && !dashboard)) {
		throw error;
	}

	const videoCollections = dashboard?.getDashboard.videoCollections || [];
	const allCollections = videoCollections.filter((vc) => !vc.onboarding);
	const inProgressCollections = videoCollections.filter(
		(vc) =>
			vc.videos?.some(
				(v) => v.stat?.percentagePlayed && v.stat?.percentagePlayed > 0,
			) &&
			!vc.onboarding &&
			!vc.stat?.completed,
	);
	const doneCollections = videoCollections.filter((vc) => vc.stat?.completed);

	const renderTab = () => {
		switch (tabIndex) {
			case 0:
				return <PickedForYou videoCollections={allCollections} />;
			case 1:
				return <InProgress videoCollections={inProgressCollections} />;
			case 2:
				return <Done videoCollections={doneCollections} />;
			default:
				return null;
		}
	};

	return (
		<>
			<Helmet title={title} />
			<SheetLayout
				titleComponent="Videos"
				topComponent={
					<Tabs
						items={[
							{ title: 'All' },
							{
								title: 'Watching',
								trailingComponent:
									!!inProgressCollections.length && (
										<TabsCount
											count={inProgressCollections.length}
										/>
									),
							},
							{
								title: 'Done',
								trailingComponent: !!doneCollections.length && (
									<TabsCount count={doneCollections.length} />
								),
							},
						]}
						onChange={(selectedIndex) => {
							setTabIndex(selectedIndex);
						}}
					/>
				}
				isLoading={isLoading}
				actions={[
					{
						icon: reel,
						onClick: () =>
							navigate(reelsUrl, {
								state: {
									linkFrom: LinkFrom.library,
								},
							}),
					},
				]}
			>
				<div className={styles['root']}>{renderTab()}</div>
			</SheetLayout>
		</>
	);
};
